import { createSlice } from '@reduxjs/toolkit';
import getInitialState from './initial-state';

const paymentsSlice = createSlice({
    name: 'payments',
    initialState: getInitialState(),
    reducers: {
        // Init
        initDataLoaded: (state, { payload }) => {
            const {
                braintreeMember,
                onboardToHyperwalletProblems,
                requireBankIdVerification,
                tinkLink,
                hyperwalletPayeeVerification
            } = payload.initData.pageSpecificData;
            const tinkOnboarding =
                payload.initData.featureSwitches['tink-onboarding'];
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    requireBankIdVerification: requireBankIdVerification
                },
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    maskedBankAccountNumber:
                        braintreeMember.maskedBankAccountNumber,
                    isBankAccountSupplied:
                        braintreeMember.isBankAccountSupplied,
                    isMemberSetForPayout: braintreeMember.isMemberSetForPayout,
                    onboardToHyperwalletProblems: onboardToHyperwalletProblems,
                    isFetching: false
                },
                tink: {
                    ...state.tink,
                    tinkOnboarding: tinkOnboarding,
                    iframeUrl: tinkLink,
                    redirectUrl: tinkLink
                },
                hyperwalletPayeeVerification
            };
        },
        // BankAccount
        updateBankClearingNumber: (state, { payload }) => {
            return {
                ...state,
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    newClearingNumber: payload,
                    bankUnknown: false,
                    errorMessage: ''
                }
            };
        },
        updateBankAccountNumber: (state, { payload }) => {
            return {
                ...state,
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    newAccountNumber: payload,
                    bankUnknown: false,
                    errorMessage: ''
                }
            };
        },
        deleteBankAccount: (state) => {
            return {
                ...state,
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    maskedBankAccountNumber: '',
                    isBankAccountSupplied: false,
                    newClearingNumber: '',
                    newAccountNumber: '',
                    errorMessage: ''
                }
            };
        },
        bankError: (state, { payload }) => {
            return {
                ...state,
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    errorMessage: payload
                }
            };
        },
        bankAccountSaved: (state, { payload }) => {
            return {
                ...state,
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    bankAccountSaved: true,
                    isBankAccountSupplied: true,
                    maskedBankAccountNumber: payload,
                    errorMessage: ''
                }
            };
        },
        bankNotMatched: (state) => {
            return {
                ...state,
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    bankUnknown: true
                }
            };
        },
        ignoreBankNotMatched: (state) => {
            return {
                ...state,
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    bankUnknown: false
                }
            };
        },
        // Tink
        initTink: (state) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    shouldInitTink: true,
                    errorMessage: ''
                }
            };
        },
        cancelTink: (state) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    shouldInitTink: false,
                    accounts: null,
                    errorMessage: ''
                }
            };
        },
        setTinkFetching: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    isFetching: payload
                }
            };
        },
        setTinkBankAccounts: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    accounts: payload,
                    isFetching: false
                }
            };
        },
        selectTinkBankAccount: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    account: payload
                }
            };
        },
        tinkCompleted: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    shouldInitTink: false,
                    accounts: null
                },
                bankAccountDetails: {
                    ...state.bankAccountDetails,
                    bankAccountSaved: true,
                    isBankAccountSupplied: true,
                    maskedBankAccountNumber: payload
                }
            };
        },
        tinkError: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    errorMessage: payload
                }
            };
        },
        // BankId
        skipBankIdAuth: (state, { payload }) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    shouldInitBankID: false,
                    bankIdCompleted: true,
                    bankIdErrorMessage: '',
                    requireBankIdVerification: false,
                    ...payload
                }
            };
        },
        beginBankIdAuth: (state, { payload }) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    shouldInitBankID: true,
                    bankIdCompleted: false,
                    bankIdErrorMessage: '',
                    ...payload
                }
            };
        },
        cancelBankIdAuth: (state) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    type: '',
                    shouldInitBankID: false,
                    bankIdInProgress: false,
                    bankIdCompleted: false,
                    bankIdErrorMessage: ''
                }
            };
        },
        bankIdInProgress: (state, { payload }) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    bankIdInProgress: true,
                    autoStartToken: payload.autoStartToken,
                    qrData: payload.qrData,
                    errorCount: 0
                }
            };
        },
        bankIdNewQrData: (state, { payload }) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    qrData: payload.qrData
                }
            };
        },
        setBankIdError: (state, { payload }) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    bankIdErrorMessage: payload,
                    bankIdInProgress: false,
                    bankIdCompleted: false
                }
            };
        },
        incrementBankIdError: (state) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    errorCount: state.bankIdVerification.errorCount + 1
                }
            };
        },
        bankIdCompleted: (state) => {
            return {
                ...state,
                bankIdVerification: {
                    ...state.bankIdVerification,
                    shouldInitBankID: false,
                    bankIdInProgress: false,
                    bankIdCompleted: true
                }
            };
        },
        // Payouts
        payoutsLoaded: (state, { payload }) => {
            return {
                ...state,
                payouts: payload.list,
                pagination: payload.pagination,
                paginationLinks: payload.paginationLinks
            };
        },
        // Update status
        setUpdateStatus: (state, { payload }) => {
            return {
                ...state,
                updateStatus: {
                    ...state.updateStatus,
                    ...payload
                }
            };
        },
        finishUpdate: (state, { payload }) => {
            return {
                ...state,
                updateStatus: {
                    ...state.updateStatus,
                    type: '',
                    isUpdating: false,
                    processCorrelationId: null,
                    errorMessage: payload?.errorMessage
                }
            };
        },
        // PayPal
        setPayPalDetails: (state, { payload }) => {
            return {
                ...state,
                paypalDetails: {
                    ...state.paypalDetails,
                    ...payload
                }
            };
        }
    }
});

export const {
    initDataLoaded,
    updateBankClearingNumber,
    updateBankAccountNumber,
    deleteBankAccount,
    bankError,
    bankAccountSaved,
    bankNotMatched,
    ignoreBankNotMatched,
    initTink,
    cancelTink,
    setTinkFetching,
    setTinkBankAccounts,
    selectTinkBankAccount,
    tinkCompleted,
    tinkError,
    skipBankIdAuth,
    beginBankIdAuth,
    cancelBankIdAuth,
    bankIdInProgress,
    bankIdNewQrData,
    setBankIdError,
    incrementBankIdError,
    bankIdCompleted,
    payoutsLoaded,
    setUpdateStatus,
    finishUpdate,
    setPayPalDetails
} = paymentsSlice.actions;

export default paymentsSlice.reducer;
